import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTopics } from "../../context/TopicContext";
import { useToast } from "../../context/ToastContext";
import styled from "styled-components";
import api from "../../services/api";
import { FaEdit, FaTrash, FaArrowUp } from "react-icons/fa";
import { Pill, PillGrid, PillRemove, EmptyState } from "../common/Pill";
import { IconButton } from "../common/controls";
import { useMode } from "../../context/ModeContext";
import theme from "../../styles/theme";

const Container = styled.div`
  width: 100%;
  /* Matches the card's bottom gap, so a full-height card does not push the
     page into scrolling by a few stray pixels. */
  padding-bottom: ${theme.space.sm};
`;

// The card is the only thing on screen while reading, so it takes the window
// minus the navbar. It scrolls internally rather than scrolling the page, which
// keeps the sticky header — and the up arrow on it — always reachable.
const Reader = styled.section`
  margin: ${theme.space.sm} ${theme.space.sm} 0;
  background: ${theme.color.surface};
  border: 1px solid ${theme.color.borderSubtle};
  border-radius: ${theme.radius.md};
  box-shadow: ${theme.shadow.panel};
  /* Fill the window below the navbar exactly. --nav-h is measured and
     published by the Navbar; the fallback covers the first paint. */
  max-height: calc(100vh - var(--nav-h, 46px) - ${theme.space.sm} - 6px);
  overflow-y: auto;
`;

const ReaderHeader = styled.header`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.space.sm};
  padding: ${theme.space.sm} ${theme.space.md};
  background: ${theme.color.surface};
  border-bottom: 1px solid ${theme.color.borderSubtle};

  h3 {
    /* Capped at half the width so a long name can never run under the
       centred back button. */
    max-width: calc(50% - ${theme.control});
    min-width: 0;
    font-size: ${theme.font.md};
    color: ${theme.color.text};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// The back-to-list button sits in the middle of the card: during a call it is
// the one control you reach for repeatedly, and the centre is a far shorter,
// more predictable pointer journey than the edge.
const CenterAction = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space.xs};
`;

const ContentViewer = styled.div`
  padding: ${theme.space.md};
  font-size: ${theme.font.md};
  color: ${theme.color.text};
  line-height: 1.6;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: ${theme.space.md} 0 ${theme.space.xs};
    font-size: ${theme.font.lg};
    color: ${theme.color.text};
  }

  p {
    margin-bottom: ${theme.space.md};
  }

  ul,
  ol {
    margin: 0 0 ${theme.space.md} ${theme.space.lg};
  }

  li {
    margin-bottom: ${theme.space.xs};
  }

  a {
    color: ${theme.color.focus};
  }

  strong {
    color: ${theme.color.text};
  }

  /* Long code or URLs must not widen the page. */
  img,
  pre,
  table {
    max-width: 100%;
  }

  pre {
    overflow-x: auto;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;

const TopicDetail = () => {
  const { id } = useParams();
  const { currentTopic, getTopic, loading: topicLoading } = useTopics();
  const { showToast } = useToast();
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  const [subtopics, setSubtopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { isEditMode } = useMode();
  const editable = isEditMode(id);

  // Back to the subtopic list. The open subtopic's pill is hidden while it is
  // open, so returning to the list means closing the card — scrolling alone
  // does nothing when it is the only subtopic.
  const backToList = useCallback(() => {
    setSelectedSubtopic(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const loadTopicAndSubtopics = async () => {
      try {
        setLoading(true);
        const topic = await getTopic(id);
        if (!topic) {
          showToast("Topic not found", "error");
          return;
        }

        const subtopicsResponse = await api.get(`/topics/${id}/subtopics`);
        const sortedSubtopics = [...(subtopicsResponse.data || [])].sort(
          (a, b) => a.name.localeCompare(b.name)
        );
        setSubtopics(sortedSubtopics);

        if (location.state?.openSubtopicId) {
          const subtopicToOpen = sortedSubtopics.find(
            (st) => st.id === location.state.openSubtopicId
          );
          if (subtopicToOpen) {
            setSelectedSubtopic(subtopicToOpen);
          }
          window.history.replaceState({}, document.title);
        }
      } catch (error) {
        console.error("Error loading topic details:", error);
        showToast(
          error.response?.data?.message || "Failed to load topic details",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadTopicAndSubtopics();
    }
  }, [id, getTopic, showToast, location.state]);

  const handleDeleteSubtopic = useCallback(
    async (subtopicId) => {
      if (!window.confirm("Are you sure you want to delete this subtopic?")) {
        return;
      }

      try {
        await api.delete(`/topics/${id}/subtopics/${subtopicId}`);
        setSubtopics((current) =>
          current.filter((st) => st.id !== subtopicId)
        );
        setSelectedSubtopic((current) =>
          current?.id === subtopicId ? null : current
        );
        showToast("Subtopic deleted successfully", "success");
      } catch (error) {
        console.error("Error deleting subtopic:", error);
        showToast(
          error.response?.data?.message || "Failed to delete subtopic",
          "error"
        );
      }
    },
    [id, showToast]
  );

  // Always start at the top when opening or closing a card, so the card (or
  // the list you came back to) is where you expect it.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [selectedSubtopic]);

  // Esc closes the reader — quicker than reaching for the mouse mid-answer.
  useEffect(() => {
    if (!selectedSubtopic) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setSelectedSubtopic(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedSubtopic]);

  if (loading || topicLoading) {
    return <EmptyState>Loading…</EmptyState>;
  }

  if (!currentTopic) {
    return <EmptyState>Topic not found.</EmptyState>;
  }

  // Reading a card and browsing the list are separate states: while a card is
  // open the whole list is hidden, however long it is, so the card always gets
  // the full window. The up arrow on the card brings the list back.
  return (
    <Container>
      {subtopics.length === 0 ? (
        <EmptyState>
          No subtopics yet. Use the + button above to add your first point.
        </EmptyState>
      ) : (
        !selectedSubtopic && (
        <PillGrid>
          {subtopics.map((subtopic) => (
            <Pill
              key={subtopic.id}
              onClick={() => setSelectedSubtopic(subtopic)}
            >
              {subtopic.name}
              {editable && (
                <PillRemove
                  as="span"
                  role="button"
                  tabIndex={0}
                  aria-label={`Delete ${subtopic.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSubtopic(subtopic.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteSubtopic(subtopic.id);
                    }
                  }}
                >
                  ×
                </PillRemove>
              )}
            </Pill>
          ))}
        </PillGrid>
        )
      )}

      {selectedSubtopic && (
        <Reader aria-label={selectedSubtopic.name}>
          <ReaderHeader>
            <h3>{selectedSubtopic.name}</h3>

            <CenterAction>
              <IconButton
                onClick={backToList}
                aria-label="Back to subtopic list"
                title="Back to subtopic list (Esc)"
              >
                <FaArrowUp />
              </IconButton>
            </CenterAction>

            {editable && (
              <HeaderActions>
                <IconButton
                  onClick={() =>
                    navigate(
                      `/topic/${id}/subtopic/${selectedSubtopic.id}/edit`
                    )
                  }
                  aria-label="Edit subtopic"
                  title="Edit subtopic"
                >
                  <FaEdit />
                </IconButton>
                <IconButton
                  $danger
                  onClick={() => handleDeleteSubtopic(selectedSubtopic.id)}
                  aria-label="Delete subtopic"
                  title="Delete subtopic"
                >
                  <FaTrash />
                </IconButton>
              </HeaderActions>
            )}
          </ReaderHeader>
          <ContentViewer
            dangerouslySetInnerHTML={{ __html: selectedSubtopic.content }}
          />
        </Reader>
      )}
    </Container>
  );
};

export default TopicDetail;
